import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, ArrowLeft, Search, X, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import type Contact from "@/model/interface/Contact";
import type { ContactEmail } from "@/model/interface/Contact";
import type ContactPhone from "@/model/interface/ContactPhone";
import { useGetContactListsQuery, useGetMembersQuery, useGetTagsQuery } from "@/store/rest_api";
import ContactConsentSection from "@/components/pages/contacts/ContactConsentSection";
import { memberName } from "@/util/member";

interface ContactFormProps {
  initialData?: Contact;
  defaultType?: ContactFormValues["type"];
  onSubmit: (data: ContactFormData) => Promise<void>;
  isSubmitting: boolean;
  submitLabel: string;
  backTo?: string;
}

export interface ContactFormData {
  type?: Contact["type"];
  firstName?: string;
  lastName?: string;
  company?: string;
  position?: string;
  address?: string;
  photo?: string;
  link?: string;
  defaultCallerId?: string;
  salesforceId?: string;
  assignedMembership?: { id?: number };
  listIds?: string[];
  tagIds?: string[];
  phones?: Pick<ContactPhone, "phoneType" | "phoneNumber">[];
  emails?: Pick<ContactEmail, "emailType" | "email">[];
}

const UNASSIGNED_OWNER = "-";

const MIN_PHONE_DIGITS = 10;

const PHONE_TYPE_LABELS: Record<NonNullable<ContactPhone["phoneType"]>, string> = {
  MOBILE: "Mobile",
  HOME: "Home",
  WORK: "Work",
  FAX: "Fax",
  OTHER: "Other",
};

const EMPTY_PHONE = { phoneType: "MOBILE" as const, phoneNumber: "" };

const EMAIL_TYPE_LABELS: Record<NonNullable<ContactEmail["emailType"]>, string> = {
  PERSONAL: "Personal",
  WORK: "Work",
  OTHER: "Other",
};

const EMPTY_EMAIL = { emailType: "PERSONAL" as const, email: "" };

function sameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id) => right.includes(id));
}

const contactSchema = z.object({
  type: z.enum(["LEAD", "CONTACT", "ACCOUNT", "OPPORTUNITY"]),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  company: z.string().optional(),
  position: z.string().optional(),
  address: z.string().optional(),
  photo: z.string().optional(),
  link: z.string().optional(),
  defaultCallerId: z.string().optional(),
  salesforceId: z.string().optional(),
  assignedMembershipId: z.string().optional(),
  phones: z.array(
    z.object({
      phoneType: z.enum(["MOBILE", "HOME", "WORK", "FAX", "OTHER"]),
      phoneNumber: z
        .string()
        .refine(
          (phoneNumber) => phoneNumber.trim() === "" || phoneNumber.replace(/\D/g, "").length >= MIN_PHONE_DIGITS,
          "Enter the full number with area code",
        ),
    }),
  ),
  emails: z.array(
    z.object({
      emailType: z.enum(["PERSONAL", "WORK", "OTHER"]),
      email: z
        .string()
        .refine(
          (email) => email.trim() === "" || z.string().email().safeParse(email.trim()).success,
          "Enter a valid email address",
        ),
    }),
  ),
});

type ContactFormValues = z.infer<typeof contactSchema>;

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function ContactForm({
  initialData,
  defaultType,
  onSubmit,
  isSubmitting,
  submitLabel,
  backTo,
}: ContactFormProps) {
  const navigate = useNavigate();
  const { data: members } = useGetMembersQuery({ status: undefined });

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      type: defaultType ?? "CONTACT",
      firstName: "",
      lastName: "",
      company: "",
      position: "",
      address: "",
      photo: "",
      link: "",
      defaultCallerId: "",
      salesforceId: "",
      phones: [EMPTY_PHONE],
      emails: [EMPTY_EMAIL],
    },
  });
  const { fields: phoneFields, append: appendPhone, remove: removePhone } = useFieldArray({
    control: form.control,
    name: "phones",
  });
  const { fields: emailFields, append: appendEmail, remove: removeEmail } = useFieldArray({
    control: form.control,
    name: "emails",
  });

  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [savedListIds, setSavedListIds] = useState<string[]>([]);
  const [savedTagIds, setSavedTagIds] = useState<string[]>([]);

  const { data: allTags = [] } = useGetTagsQuery({});

  const [listQ, setListQ] = useState("");
  const debouncedListQ = useDebouncedValue(listQ.trim(), 250);
  const [listPage, setListPage] = useState(0);
  const [listSize, setListSize] = useState(10);

  const { data: listsResp, isFetching: listsFetching } = useGetContactListsQuery({
    page: listPage,
    size: listSize,
    q: debouncedListQ || undefined,
    sort: "createdAt,desc",
  });

  const lists = listsResp?.items ?? [];
  const listsLast = listsResp?.last ?? true;

  const selectedListObjects = useMemo(() => {
    const fromInitial =
      initialData?.contactListLinks?.map((l) => l.contactList).filter((x): x is NonNullable<typeof x> => !!x) ?? [];

    const map = new Map<string, { id: string; name: string }>();
    for (const list of fromInitial) {
      map.set(list.id, { id: list.id, name: list.name });
    }

    for (const list of lists) {
      map.set(list.id, { id: list.id, name: list.name });
    }

    return selectedListIds.map((id) => map.get(id)).filter((x): x is { id: string; name: string } => !!x);
  }, [selectedListIds, initialData?.contactListLinks, lists]);

  const toggleList = (id: string) => {
    setSelectedListIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const removeList = (id: string) => {
    setSelectedListIds((prev) => prev.filter((x) => x !== id));
  };

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const removeTag = (id: string) => {
    setSelectedTagIds((prev) => prev.filter((x) => x !== id));
  };

  const ownerLabel = (membershipId?: string) => {
    const member = members?.find((candidate) => String(candidate.id) === membershipId);
    if (!member) {
      return "Unassigned";
    }
    return memberName(member, "");
  };

  const handleSubmit = async (values: ContactFormValues) => {
    await onSubmit({
      type: values.type,
      firstName: values.firstName?.trim() ?? "",
      lastName: values.lastName?.trim() ?? "",
      company: values.company?.trim() ?? "",
      position: values.position?.trim() ?? "",
      address: values.address?.trim() ?? "",
      photo: values.photo?.trim() ?? "",
      link: values.link?.trim() ?? "",
      defaultCallerId: values.defaultCallerId?.trim() ?? "",
      salesforceId: values.salesforceId?.trim() ?? "",
      assignedMembership:
        values.assignedMembershipId && values.assignedMembershipId !== UNASSIGNED_OWNER
          ? { id: Number(values.assignedMembershipId) }
          : {},
      listIds: selectedListIds,
      tagIds: selectedTagIds,
      phones: values.phones
        .filter((phone) => phone.phoneNumber.trim() !== "")
        .map((phone) => ({ phoneType: phone.phoneType, phoneNumber: phone.phoneNumber.trim() })),
      emails: values.emails
        .filter((contactEmail) => contactEmail.email.trim() !== "")
        .map((contactEmail) => ({ emailType: contactEmail.emailType, email: contactEmail.email.trim() })),
    });
  };

  useEffect(() => {
    if (!initialData) {
      return;
    }

    form.reset({
      type: initialData.type ?? "CONTACT",
      firstName: initialData.firstName ?? "",
      lastName: initialData.lastName ?? "",
      company: initialData.company ?? "",
      position: initialData.position ?? "",
      address: initialData.address ?? "",
      photo: initialData.photo ?? "",
      link: initialData.link ?? "",
      defaultCallerId: initialData.defaultCallerId ?? "",
      salesforceId: initialData.salesforceId ?? "",
      assignedMembershipId: initialData.assignedMembership?.id ? String(initialData.assignedMembership.id) : "",
      phones: initialData.phones?.length
        ? initialData.phones.map((phone) => ({
            phoneType: phone.phoneType ?? "MOBILE",
            phoneNumber: phone.phoneNumber || phone.phoneNumberE164 || "",
          }))
        : [EMPTY_PHONE],
      emails: initialData.emails?.length
        ? initialData.emails.map((contactEmail) => ({
            emailType: contactEmail.emailType ?? "PERSONAL",
            email: contactEmail.email ?? "",
          }))
        : [EMPTY_EMAIL],
    });

    const initialIds = initialData.contactListLinks?.map((l) => l.contactList?.id).filter(Boolean) as
      | string[]
      | undefined;

    setSelectedListIds(initialIds ?? []);
    setSavedListIds(initialIds ?? []);

    const initialTagIds = initialData.contactTags?.map((ct) => ct.tag?.id).filter(Boolean) as
      | string[]
      | undefined;
    setSelectedTagIds(initialTagIds ?? []);
    setSavedTagIds(initialTagIds ?? []);
  }, [initialData]);

  useEffect(() => {
    setListPage(0);
  }, [debouncedListQ, listSize]);

  const hasChanges =
    form.formState.isDirty || !sameIds(selectedListIds, savedListIds) || !sameIds(selectedTagIds, savedTagIds);
  const canSubmit = !initialData || hasChanges;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="flex items-center justify-end">
          <Button type="submit" disabled={isSubmitting || !canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        </div>
        {/* Basic info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Manage contact identity and company details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField control={form.control} name="type" render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="CONTACT">Contact</SelectItem>
                    <SelectItem value="LEAD">Lead</SelectItem>
                    <SelectItem value="ACCOUNT">Account</SelectItem>
                    <SelectItem value="OPPORTUNITY">Opportunity</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="assignedMembershipId" render={({ field }) => (
              <FormItem>
                <FormLabel>Owner</FormLabel>
                {members ? (
                  <Select value={field.value || UNASSIGNED_OWNER} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Unassigned">{ownerLabel(field.value)}</SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED_OWNER}>Unassigned</SelectItem>
                      {members.map((member) => (
                        <SelectItem key={member.id} value={String(member.id)}>
                          {memberName(member)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Skeleton className="h-9 w-full" />
                )}
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="firstName" render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="lastName" render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="company" render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="position" render={({ field }) => (
                <FormItem>
                  <FormLabel>Position</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="address" render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl><Textarea rows={3} {...field} /></FormControl>
              </FormItem>
            )} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Phone numbers</CardTitle>
            <CardDescription>The numbers you can call and text this person on</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {phoneFields.map((phoneField, index) => (
              <div key={phoneField.id} className="flex items-start gap-3">
                <FormField control={form.control} name={`phones.${index}.phoneType`} render={({ field }) => (
                  <FormItem className="w-32 shrink-0">
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger aria-label="Phone type"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {Object.entries(PHONE_TYPE_LABELS).map(([phoneType, label]) => (
                          <SelectItem key={phoneType} value={phoneType}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <FormField control={form.control} name={`phones.${index}.phoneNumber`} render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 123-4567" aria-label="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  title="Remove number"
                  disabled={phoneFields.length === 1}
                  onClick={() => removePhone(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => appendPhone(EMPTY_PHONE)}>
              <Plus className="mr-2 h-4 w-4" />
              Add phone
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Emails</CardTitle>
            <CardDescription>The first email is the one workflows send to</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {emailFields.map((emailField, index) => (
              <div key={emailField.id} className="flex items-start gap-3">
                <FormField control={form.control} name={`emails.${index}.emailType`} render={({ field }) => (
                  <FormItem className="w-32 shrink-0">
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger aria-label="Email type"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {Object.entries(EMAIL_TYPE_LABELS).map(([emailType, label]) => (
                          <SelectItem key={emailType} value={emailType}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <FormField control={form.control} name={`emails.${index}.email`} render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input type="email" placeholder="name@example.com" aria-label="Email address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <Button type="button" variant="ghost" size="icon" title="Remove email" onClick={() => removeEmail(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => appendEmail(EMPTY_EMAIL)}>
              <Plus className="mr-2 h-4 w-4" />
              Add email
            </Button>
          </CardContent>
        </Card>

        {/* Lists */}
        <Card>
          <CardHeader>
            <CardTitle>Lists</CardTitle>
            <CardDescription>Optionally add this contact to one or more lists</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedListObjects.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedListObjects.map((l) => (
                  <Badge key={l.id} variant="secondary" className="gap-1 pr-1">
                    <span className="max-w-55 truncate">{l.name}</span>
                    <button
                      type="button"
                      className="ml-1 rounded hover:bg-muted/60 p-0.5"
                      onClick={() => removeList(l.id)}
                      aria-label={`Remove ${l.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Not in any lists yet.</div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={listQ}
                onChange={(e) => setListQ(e.target.value)}
                placeholder="Search lists..."
                className="pl-9"
              />
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-56 overflow-y-auto">
                {listsFetching && lists.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">Loading lists…</div>
                ) : lists.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">No lists found.</div>
                ) : (
                  <div className="divide-y">
                    {lists.map((l) => {
                      const active = selectedListIds.includes(l.id);
                      return (
                        <button
                          type="button"
                          key={l.id}
                          onClick={() => toggleList(l.id)}
                          className={`w-full text-left p-3 transition flex items-start justify-between gap-3 ${
                            active ? "bg-primary/10" : "hover:bg-muted/50"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="font-medium truncate">{l.name}</div>
                            {l.description && (
                              <div className="text-sm text-muted-foreground truncate">{l.description}</div>
                            )}
                          </div>

                          <Badge variant={active ? "default" : "outline"} className="shrink-0">
                            {active ? "Selected" : "Select"}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 p-3 border-t bg-muted/20">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={listPage === 0 || lists.length === 0}
                    onClick={() => setListPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={listsLast || lists.length === 0}
                    onClick={() => setListPage((p) => p + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>

                  {listsFetching && <span className="text-xs text-muted-foreground">Updating…</span>}
                </div>

                <Select value={String(listSize)} onValueChange={(v) => setListSize(Number(v))}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 / page</SelectItem>
                    <SelectItem value="20">20 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tags */}
        <Card>
          <CardHeader>
            <CardTitle>Tags</CardTitle>
            <CardDescription>Categorise this contact with tags</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedTagIds.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedTagIds.map((id) => {
                  const tag = allTags.find((t) => t.id === id);
                  if (!tag) return null;
                  return (
                    <Badge
                      key={id}
                      className="gap-1 pr-1 text-white"
                      style={{ backgroundColor: tag.color }}
                    >
                      <span className="max-w-40 truncate">{tag.name}</span>
                      <button
                        type="button"
                        className="ml-1 rounded hover:bg-white/20 p-0.5"
                        onClick={() => removeTag(id)}
                        aria-label={`Remove ${tag.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No tags assigned.</div>
            )}

            {allTags.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-56 overflow-y-auto divide-y">
                  {allTags.map((t) => {
                    const active = selectedTagIds.includes(t.id);
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => toggleTag(t.id)}
                        className={`w-full text-left p-3 transition flex items-center gap-3 ${
                          active ? "bg-primary/10" : "hover:bg-muted/50"
                        }`}
                      >
                        <span
                          className="h-4 w-4 rounded-full shrink-0"
                          style={{ backgroundColor: t.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{t.name}</div>
                          {t.description && (
                            <div className="text-sm text-muted-foreground truncate">{t.description}</div>
                          )}
                        </div>
                        <Badge variant={active ? "default" : "outline"} className="shrink-0">
                          {active ? "Selected" : "Select"}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                No tags created yet. Create tags in Settings.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Consent (only when editing) */}
        {initialData?.id && <ContactConsentSection contact={initialData} />}

        <Card>
          <CardHeader>
            <CardTitle>Extras</CardTitle>
            <CardDescription>Optional profile and call settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="defaultCallerId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Caller ID</FormLabel>
                  <FormControl><Input placeholder="+1..." {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="salesforceId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Salesforce ID</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="photo" render={({ field }) => (
              <FormItem>
                <FormLabel>Photo URL</FormLabel>
                <FormControl><Input placeholder="https://..." {...field} /></FormControl>
              </FormItem>
            )} />

            <FormField control={form.control} name="link" render={({ field }) => (
              <FormItem>
                <FormLabel>Website</FormLabel>
                <FormControl><Input placeholder="https://example.com" {...field} /></FormControl>
              </FormItem>
            )} />
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" onClick={() => navigate(backTo ?? "/contacts")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Contacts
          </Button>

          <Button type="submit" disabled={isSubmitting || !canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
